import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, ChevronRight, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Search, X } from "lucide-react";

import IconButton from "./IconButton";
import WSMLogo from "./WSMLogo";

const ignorePackSelection = () => undefined;
const emptyPackSearch = async () => ({ data: [] });

const SidebarItem = ({ activePath, collapsed, item }) => {
  const hasChildren = Boolean(item.children?.length);
  const childIsActive = item.children?.some((child) => child.href === activePath);
  const [expanded, setExpanded] = useState(item.defaultExpanded || childIsActive);
  const expandedBeforeCollapseRef = useRef(expanded);
  const previousCollapsedRef = useRef(collapsed);
  const groupRef = useRef(null);
  const Icon = item.icon;

  useLayoutEffect(() => {
    if (previousCollapsedRef.current === collapsed) return;

    if (collapsed) {
      expandedBeforeCollapseRef.current = expanded;
      setExpanded(false);
    } else {
      setExpanded(expandedBeforeCollapseRef.current || childIsActive || item.defaultExpanded);
    }

    previousCollapsedRef.current = collapsed;
  }, [childIsActive, collapsed, expanded, item.defaultExpanded]);

  useEffect(() => {
    if (!collapsed || !expanded || !hasChildren) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!groupRef.current?.contains(event.target)) setExpanded(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setExpanded(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [collapsed, expanded, hasChildren]);

  if (hasChildren) {
    return (
      <li className="wsm-sidebar__group" ref={groupRef}>
        <button
          aria-label={collapsed ? item.label : undefined}
          aria-expanded={expanded}
          aria-haspopup={collapsed ? "menu" : undefined}
          className={["wsm-sidebar__item", childIsActive && "wsm-sidebar__item--parent-active"].filter(Boolean).join(" ")}
          onClick={() => setExpanded((current) => !current)}
          title={collapsed ? item.label : undefined}
          type="button"
        >
          <Icon aria-hidden="true" />
          {!collapsed && <span>{item.label}</span>}
          {!collapsed && (expanded ? <ChevronDown aria-hidden="true" className="wsm-sidebar__chevron" /> : <ChevronRight aria-hidden="true" className="wsm-sidebar__chevron" />)}
        </button>
        {expanded && !collapsed && (
          <ul className="wsm-sidebar__children">
            {item.children.map((child) => {
              const ChildIcon = child.icon;
              return (
                <li key={child.href}>
                  <NavLink className={["wsm-sidebar__item", "wsm-sidebar__item--child", child.href === activePath && "wsm-sidebar__item--active"].filter(Boolean).join(" ")} to={child.href}>
                    <ChildIcon aria-hidden="true" />
                    <span>{child.label}</span>
                    {child.children && <ChevronRight aria-hidden="true" className="wsm-sidebar__chevron" />}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        )}
        {expanded && collapsed && (
          <div className="wsm-sidebar__flyout" role="menu">
            <strong>{item.label}</strong>
            <ul>
              {item.children.map((child) => {
                const ChildIcon = child.icon;
                return (
                  <li key={child.href}>
                    <NavLink
                      className={["wsm-sidebar__item", "wsm-sidebar__item--child", child.href === activePath && "wsm-sidebar__item--active"].filter(Boolean).join(" ")}
                      onClick={() => setExpanded(false)}
                      role="menuitem"
                      to={child.href}
                    >
                      <ChildIcon aria-hidden="true" />
                      <span>{child.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </li>
    );
  }

  return (
    <li>
      <NavLink
        className={["wsm-sidebar__item", item.href === activePath && "wsm-sidebar__item--active"].filter(Boolean).join(" ")}
        title={collapsed ? item.label : undefined}
        to={item.href}
      >
        <Icon aria-hidden="true" />
        {!collapsed && <span>{item.label}</span>}
      </NavLink>
    </li>
  );
};

SidebarItem.propTypes = {
  activePath: PropTypes.string.isRequired,
  collapsed: PropTypes.bool.isRequired,
  item: PropTypes.shape({
    children: PropTypes.array,
    defaultExpanded: PropTypes.bool,
    href: PropTypes.string,
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
};

export const Sidebar = ({ activePath, collapsed, items, mobileOpen, onClose }) => (
  <>
    {mobileOpen && <button aria-label="Close navigation" className="wsm-shell__scrim" onClick={onClose} type="button" />}
    <aside className={["wsm-sidebar", collapsed && "wsm-sidebar--collapsed", mobileOpen && "wsm-sidebar--mobile-open"].filter(Boolean).join(" ")}>
      <div className="wsm-sidebar__brand">
        <span className="wsm-sidebar__mark"><WSMLogo /></span>
        {!collapsed && <span className="wsm-sidebar__brand-copy"><strong>RailHub</strong><small>Worksite Manager</small></span>}
        <IconButton aria-label="Close navigation" className="wsm-sidebar__mobile-close" icon={X} onClick={onClose} size="compact" />
      </div>
      <nav aria-label="Primary navigation" className="wsm-sidebar__nav">
        <ul>{items.map((item) => <SidebarItem activePath={activePath} collapsed={collapsed} item={item} key={item.label} />)}</ul>
      </nav>
    </aside>
  </>
);

Sidebar.propTypes = {
  activePath: PropTypes.string.isRequired,
  collapsed: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(SidebarItem.propTypes.item).isRequired,
  mobileOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="wsm-breadcrumbs">
    <ol>
      {items.map((item, index) => (
        <li key={item.label}>
          {index > 0 && <ChevronRight aria-hidden="true" />}
          {item.href && index < items.length - 1 ? <Link to={item.href}>{item.label}</Link> : <span aria-current={index === items.length - 1 ? "page" : undefined}>{item.label}</span>}
        </li>
      ))}
    </ol>
  </nav>
);

Breadcrumbs.propTypes = { items: PropTypes.arrayOf(PropTypes.shape({ href: PropTypes.string, label: PropTypes.string.isRequired })).isRequired };

export const PageHeader = ({ actions, breadcrumbs = [], description, title }) => (
  <header className="wsm-page-header">
    {breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} />}
    <div className="wsm-page-header__row">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="wsm-page-header__actions">{actions}</div>}
    </div>
  </header>
);

PageHeader.propTypes = {
  actions: PropTypes.node,
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    href: PropTypes.string,
    label: PropTypes.string.isRequired,
  })),
  description: PropTypes.string,
  title: PropTypes.string.isRequired,
};

const ApplicationShell = ({ activePath, children, items, onLogout, onPackSelect = ignorePackSelection, onSearchPacks = emptyPackSearch, user }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [packQuery, setPackQuery] = useState("");
  const [packResults, setPackResults] = useState([]);
  const [packSearchLoading, setPackSearchLoading] = useState(false);
  const [packSearchOpen, setPackSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const userMenuRef = useRef(null);
  useEffect(() => { if (mobileOpen) document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, [mobileOpen]);
  useEffect(() => {
    if (!userMenuOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!userMenuRef.current?.contains(event.target)) setUserMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setUserMenuOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [userMenuOpen]);
  useEffect(() => {
    const query = packQuery.trim();
    if (query.length < 2) {
      setPackResults([]);
      setPackSearchLoading(false);
      setPackSearchOpen(false);
      return undefined;
    }

    let active = true;
    setPackSearchLoading(true);
    const timeout = window.setTimeout(() => {
      onSearchPacks(query)
        .then((response) => {
          if (!active) return;
          setPackResults(response?.data || []);
          setPackSearchOpen(true);
        })
        .catch(() => {
          if (!active) return;
          setPackResults([]);
          setPackSearchOpen(true);
        })
        .finally(() => {
          if (active) setPackSearchLoading(false);
        });
    }, 300);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [onSearchPacks, packQuery]);
  useEffect(() => {
    if (!packSearchOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!searchRef.current?.contains(event.target)) setPackSearchOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [packSearchOpen]);

  const initials = user.name.split(" ").filter(Boolean).map((part) => part[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className={["wsm-shell", collapsed && "wsm-shell--collapsed"].filter(Boolean).join(" ")}>
      <Sidebar activePath={activePath} collapsed={collapsed} items={items} mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <header className="wsm-topbar">
        <IconButton aria-label="Open navigation" className="wsm-topbar__mobile-menu" icon={Menu} onClick={() => setMobileOpen(true)} />
        <IconButton aria-label={collapsed ? "Expand navigation" : "Collapse navigation"} className="wsm-topbar__collapse" icon={collapsed ? PanelLeftOpen : PanelLeftClose} onClick={() => setCollapsed((current) => !current)} />
        <div className="wsm-topbar__search" ref={searchRef}>
          <Search aria-hidden="true" />
          <label className="wsm-sr-only" htmlFor="pack-global-search">Search packs by reference, possession number, WON item or keyword</label>
          <input
            aria-autocomplete="list"
            aria-controls="pack-global-search-results"
            aria-expanded={packSearchOpen}
            autoComplete="off"
            id="pack-global-search"
            onChange={(event) => setPackQuery(event.target.value)}
            onFocus={() => packQuery.trim().length >= 2 && setPackSearchOpen(true)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setPackSearchOpen(false);
              if (event.key === "Enter" && packResults[0]) {
                event.preventDefault();
                onPackSelect(packResults[0]);
                setPackSearchOpen(false);
              }
            }}
            placeholder="Search reference, possession, WON item or keyword"
            role="combobox"
            title="Search by pack reference, possession number, WON item or keyword"
            type="search"
            value={packQuery}
          />
          {packSearchOpen && (
            <div className="wsm-topbar__search-results" id="pack-global-search-results" role="listbox">
              {packSearchLoading && <p role="status">Searching packs...</p>}
              {!packSearchLoading && packResults.length === 0 && <p>No packs found</p>}
              {!packSearchLoading && packResults.map((pack) => (
                <button
                  key={pack.id}
                  onClick={() => {
                    onPackSelect(pack);
                    setPackSearchOpen(false);
                  }}
                  aria-selected="false"
                  role="option"
                  type="button"
                >
                  <span><strong>{pack.pack_reference}</strong><small>{pack.possession_no || "No possession reference"}</small></span>
                  <small>{pack.status}</small>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="wsm-topbar__user-menu" ref={userMenuRef}>
          <button aria-expanded={userMenuOpen} aria-haspopup="menu" className="wsm-topbar__user" onClick={() => setUserMenuOpen((open) => !open)} type="button">
            <span className="wsm-topbar__avatar">{initials}</span>
            <span className="wsm-topbar__user-copy"><strong>{user.name}</strong><small>{user.role}</small></span>
            <ChevronDown aria-hidden="true" />
          </button>
          {userMenuOpen && (
            <div className="wsm-topbar__user-popover" role="menu">
              <button onClick={() => { setUserMenuOpen(false); onLogout(); }} role="menuitem" type="button">
                <LogOut aria-hidden="true" />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </header>
      <main className="wsm-shell__main">{children}</main>
    </div>
  );
};

ApplicationShell.propTypes = {
  activePath: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  items: Sidebar.propTypes.items,
  onLogout: PropTypes.func.isRequired,
  onPackSelect: PropTypes.func,
  onSearchPacks: PropTypes.func,
  user: PropTypes.shape({ name: PropTypes.string.isRequired, role: PropTypes.string.isRequired }).isRequired,
};

export default ApplicationShell;
